import { useCallback, useRef, useState, useEffect } from "react";
import Cropper from "react-easy-crop";
import { useDispatch, useSelector } from "react-redux";
import { createPost } from "../../functions/post";
import { uploadImages } from "../../functions/uploadImages";
import { updateprofilePicture } from "../../functions/user";
import getCroppedImg from "../../helpers/getCroppedImg";
import PulseLoader from "react-spinners/PulseLoader";
import Cookies from "js-cookie";

export default function UpdateProfilePicture({
  setImage,
  image,
  setError,
  setShow,
  pRef,
}) {
  const dispatch = useDispatch();
  const [description] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showPostConfirmation, setShowPostConfirmation] = useState(false);
  const [pendingProfileUpdate, setPendingProfileUpdate] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const slider = useRef(null);
  const timeoutRef = useRef(null);
  const { user } = useSelector((state) => ({ ...state }));
  const [loading, setLoading] = useState(false);
  // Determine account privacy from user or localStorage fallback
  const accountPrivacy = (user && (user.privacy || user?.profile?.privacy)) || localStorage.getItem("accountPrivacy") || "public";

  // Show success message with proper cleanup
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      setShowSuccessAlert(false);
      setSuccessMessage("");
      timeoutRef.current = null;
    }, 4000);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const zoomIn = () => {
    slider.current.stepUp();
    setZoom(slider.current.value);
  };

  const zoomOut = () => {
    slider.current.stepDown();
    setZoom(slider.current.value);
  };

  const rotateLeft = () => setRotation((r) => (r - 90 + 360) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  const getCroppedImage = useCallback(
    async (show) => {
      try {
        const img = await getCroppedImg(image, croppedAreaPixels, rotation);
        if (show) {
          setZoom(1);
          setCrop({ x: 0, y: 0 });
          setRotation(0);
          setImage(img);
        } else {
          return img;
        }
      } catch (error) {
        console.log(error);
      }
    },
    [croppedAreaPixels, image, rotation, setImage]
  );

  const updateProfilePictureOnly = async (imageData) => {
    try {
      console.log("Updating profile picture only:", imageData);
      const updated_picture = await updateprofilePicture(
        imageData.url,
        user.token
      );
      console.log("Profile picture update result:", updated_picture);
      if (updated_picture === "ok") {
        setImage("");
        pRef.current.style.backgroundImage = `url(${imageData.url})`;
        Cookies.set(
          "user",
          JSON.stringify({
            ...user,
            picture: imageData.url,
          })
        );
        dispatch({
          type: "UPDATEPICTURE",
          payload: imageData.url,
        });
        showSuccess("Profile picture updated successfully!");
        setShow(false);
        return true;
      } else {
        console.error("Profile picture update failed:", updated_picture);
        setError(updated_picture);
        return false;
      }
    } catch (error) {
      console.error("Profile picture update error:", error);
      setError(error.response?.data?.message || "Failed to update profile picture");
      return false;
    }
  };

  const updateProfilePictureWithPost = async (imageData) => {
    try {
      console.log("Updating profile picture with post:", imageData);
      const updated_picture = await updateprofilePicture(
        imageData.url,
        user.token
      );
      console.log("Profile picture update result:", updated_picture);
      if (updated_picture === "ok") {
        console.log("Creating post with description:", description);
        const new_post = await createPost(
          "profilePicture",
          null,
          description,
          [imageData],
          user.id,
          user.token
        );
        console.log("Post creation result:", new_post);
        if (new_post.status === "ok") {
          setImage("");
          pRef.current.style.backgroundImage = `url(${imageData.url})`;
          Cookies.set(
            "user",
            JSON.stringify({
              ...user,
              picture: imageData.url,
            })
          );
          dispatch({
            type: "UPDATEPICTURE",
            payload: imageData.url,
          });
          showSuccess("Profile picture uploaded and post created successfully!");
          setShow(false);
          return true;
        } else {
          console.error("Post creation failed:", new_post.message);
          setError(new_post.message);
          return false;
        }
      } else {
        console.error("Profile picture update failed:", updated_picture);
        setError(updated_picture);
        return false;
      }
    } catch (error) {
      console.error("Profile picture with post error:", error);
      setError(error.response?.data?.message || "Failed to update profile picture");
      return false;
    }
  };

  const handleSaveProfilePicture = async () => {
    try {
      setLoading(true);
      let img = await getCroppedImage();
      let blob = await fetch(img).then((b) => b.blob());
      const path = `${user.username}/profile_pictures`;
      let formData = new FormData();
      formData.append("file", blob);
      formData.append("path", path);
      const res = await uploadImages(formData, path, user.token);
      
      // Store the image data and show confirmation dialog
      setPendingProfileUpdate(res[0]);
      setShowPostConfirmation(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.response?.data?.message || "Failed to upload image");
    }
  };

  const handleConfirmCreatePost = async () => {
    setShowPostConfirmation(false);
    setLoading(true);
    await updateProfilePictureWithPost(pendingProfileUpdate);
    setLoading(false);
    setPendingProfileUpdate(null);
  };

  const handleSkipPost = async () => {
    setShowPostConfirmation(false);
    setLoading(true);
    await updateProfilePictureOnly(pendingProfileUpdate);
    setLoading(false);
    setPendingProfileUpdate(null);
  };

  const handleCancelConfirmation = () => {
    setShowPostConfirmation(false);
    setPendingProfileUpdate(null);
  };

  return (
    <>
      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="profile_success_alert">
          <div className="profile_success_content">
            <i className="success_check_icon">✓</i>
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      <div className="postBox update_img">
        <div className="box_header">
          <div className="small_circle" onClick={() => setImage("")}>
            <i className="exit_icon"></i>
          </div>
          <span>Edit profile picture</span>
        </div>
        <div className="update_center">
          <div className="crooper" style={{ position: "relative" }}>
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1 / 1}
              cropShape="round"
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              showGrid={false}
              style={{
                containerStyle: {
                  background: 'transparent',
                },
                mediaStyle: {
                  background: 'transparent',
                },
              }}
            />
          </div>
          <div className="slider">
            <div className="slider_circle hover1" onClick={() => zoomOut()}>
              <i className="minus_icon"></i>
            </div>
            <input
              type="range"
              min={1}
              max={3}
              step={0.2}
              ref={slider}
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
            />
            <div className="slider_circle hover1" onClick={() => zoomIn()}>
              <i className="plus_icon"></i>
            </div>
            {/* Rotate controls */}
            <div className="slider_circle hover1" onClick={rotateLeft} title="Rotate left">
              <i className="refresh_icon" style={{ transform: "scaleX(-1)" }}></i>
            </div>
            <div className="slider_circle hover1" onClick={rotateRight} title="Rotate right">
              <i className="refresh_icon"></i>
            </div>
          </div>
        </div>
        <div className="flex_p_t">
          {accountPrivacy === 'private' ? (
            <>
              <img src="/icons/lock.png" alt="" style={{ width: 16, height: 16, marginRight: 6 }} />
              Your profile picture is private
            </>
          ) : (
            <>
              <i className="public_icon"></i>
              Your profile picture is public
            </>
          )}
        </div>
        <div className="update_submit_wrap">
          <div className="blue_link" onClick={() => setImage("")}>
            Cancel
          </div>
          <button
            className="blue_btn"
            disabled={loading}
            onClick={handleSaveProfilePicture}
          >
            {loading ? <PulseLoader color="#fff" size={5} /> : "Save"}
          </button>
        </div>
      </div>

      {/* Post Confirmation Modal */}
      {showPostConfirmation && (
        <div className="blur profile_post_confirmation_overlay">
          <div className="postBox profile_post_confirmation_modal">
            <div className="box_header">
              <span>Create a publication with your new profile picture?</span>
            </div>
            <div className="profile_post_confirmation_content">
              <div className="profile_post_confirmation_image">
                <img src={pendingProfileUpdate?.url} alt="Profile preview" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '50%' }} />
              </div>
              <p>Do you want to share your new profile picture as a publication so your friends can see it?</p>
              {description && (
                <div className="profile_post_confirmation_description">
                  <strong>Description:</strong> {description}
                </div>
              )}
            </div>
            <div className="profile_post_confirmation_actions">
              <button 
                className="gray_btn"
                onClick={handleCancelConfirmation}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="light_gray_btn"
                onClick={handleSkipPost}
                disabled={loading}
              >
                {loading ? <PulseLoader color="#333" size={5} /> : "Skip Publication"}
              </button>
              <button 
                className="blue_btn"
                onClick={handleConfirmCreatePost}
                disabled={loading}
              >
                {loading ? <PulseLoader color="#fff" size={5} /> : "Yes, Create publication"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
